/*
 * Figma `48:29` — Section Tab. State: Default, Active, Disabled.
 *
 * A real tablist, not a row of buttons: `role="tablist"` with `aria-selected`, arrow-key movement
 * between tabs and a single tab stop, because that is what a screen reader and a keyboard user are
 * promised by the role. Rendering the role without the behaviour is worse than rendering no role.
 *
 * A destination holding one section renders nothing at all. A single tab is a label pretending to
 * be a control, and the page heading already says the same word.
 */
import { useRef } from 'react';

export interface SectionTabItem<K extends string = string> {
  key: K;
  label: string;
  /** A count shown beside the label, e.g. slips waiting. */
  count?: number;
  disabled?: boolean;
}

export function SectionTabs<K extends string>({ items, active, onSelect, label }: {
  items: readonly SectionTabItem<K>[];
  active: K;
  onSelect: (key: K) => void;
  /** Names the tablist — the destination this row navigates. */
  label: string;
}) {
  const refs = useRef(new Map<string, HTMLButtonElement>());
  if (items.length < 2) return null;

  const move = (from: number, delta: number) => {
    const enabled = items.filter((i) => !i.disabled);
    if (enabled.length === 0) return;
    const at = enabled.findIndex((i) => i.key === items[from].key);
    const next = enabled[(((at === -1 ? 0 : at) + delta) % enabled.length + enabled.length) % enabled.length];
    refs.current.get(next.key)?.focus();
    onSelect(next.key);
  };

  return (
    <div className="jt-tabs" role="tablist" aria-label={label}>
      {items.map((item, index) => {
        const selected = item.key === active;
        return (
          <button
            key={item.key}
            ref={(el) => { if (el) refs.current.set(item.key, el); else refs.current.delete(item.key); }}
            type="button"
            role="tab"
            className="jt-tab"
            aria-selected={selected}
            aria-disabled={item.disabled || undefined}
            /* One tab stop for the row: Tab reaches the tablist, arrows move inside it. */
            tabIndex={selected ? 0 : -1}
            onClick={() => { if (!item.disabled) onSelect(item.key); }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); move(index, 1); }
              if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); move(index, -1); }
            }}
          >
            {item.label}
            {item.count !== undefined && item.count > 0 && <span className="jt-tab-count">{item.count}</span>}
          </button>
        );
      })}
    </div>
  );
}
